import { useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, Wine, ShoppingBasket } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDistanceKm } from "@/lib/map/formatDistance";
import type { ExploreResultItem } from "@/types/exploreResult";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { ShareButton } from "@/components/share/ShareButton";
import type { FavoriteKind } from "@/lib/favorites/types";
import {
  formatTimingBadge,
  formatTimeRow,
} from "@/lib/events/eventCardUtils";

interface MobileExploreCardProps {
  item: ExploreResultItem;
}

/**
 * Unified card for mobile Explore list view.
 * UX rule: List = Go. Map = Peek. See design-rules/ux-rules/explore.md
 */
export function MobileExploreCard({ item }: MobileExploreCardProps) {
  const navigate = useNavigate();
  const distanceDisplay = item._distanceKm != null ? formatDistanceKm(item._distanceKm) : null;
  const favKind: FavoriteKind = item.kind === "activity" ? "activity" : item.kind === "producer" ? "producer" : "place";
  const shareKind = item.kind === "activity" ? "activity" as const : item.kind === "producer" ? "producer" as const : "place" as const;

  const isProducer = item.kind === "producer";
  const isActivity = item.kind === "activity";

  // Canonical timing for activities
  const timingMode = item.timingMode || "date_time";
  const timingBadge = isActivity && item.startDate
    ? formatTimingBadge(timingMode, item.startDate, item.outingTags)
    : item.dateLabel;
  const timeRow = isActivity && item.startDate
    ? formatTimeRow(timingMode, item.startDate, item.endDate)
    : null;

  // Image aspect: Producer 3:2, others 16:9
  const aspectClass = isProducer ? "aspect-[3/2]" : "aspect-[16/9]";

  // Sale signal chips (unified for all kinds)
  const showFarmShop = isActivity ? item.includesFarmSale : (item.hasFarmShop ?? false);
  const showDryck = isActivity ? item.includesFarmSaleAlcohol : (item.hasFarmSaleAlcohol ?? false);
  const hasSignals = showFarmShop || showDryck;

  return (
    <div
      role="button"
      tabIndex={0}
      className="w-full text-left active:bg-muted/30 transition-colors cursor-pointer"
      onClick={() => navigate(item.href)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(item.href); }}
    >
      {/* Image */}
      <div className={`relative ${aspectClass} bg-muted overflow-hidden`}>
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/60">
            <span className="text-3xl text-muted-foreground/30">{item.title.charAt(0)}</span>
          </div>
        )}

        {/* Timing badge (activities only) */}
        {timingBadge && (
          <div className="absolute top-3 left-3">
            <div className="bg-background/95 backdrop-blur-sm rounded-lg px-2.5 py-1 shadow-sm">
              <span className="text-xs font-semibold text-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {timingBadge}
              </span>
            </div>
          </div>
        )}

        {/* Distance badge */}
        {distanceDisplay && (
          <div className="absolute top-3 right-14">
            <span className="text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground px-2 py-1 rounded-full flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {distanceDisplay}
            </span>
          </div>
        )}

        {/* Share + Favorite */}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <ShareButton kind={shareKind} slug={item.slug} title={item.title} size="sm" />
          <FavoriteButton
            kind={favKind}
            entityId={item.id}
            slug={item.slug || undefined}
            title={item.title}
            imagePath={item.image || undefined}
            size="sm"
          />
        </div>

        {/* Sold out overlay */}
        {item.spotsInfo === "Fullbokat" && (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
            <Badge variant="secondary">Fullbokat</Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-1">
        {/* Sale signal chips */}
        {hasSignals && (
          <div className="flex items-center gap-2 mb-0.5">
            {showFarmShop && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/85 font-medium">
                <ShoppingBasket aria-hidden className="h-3.5 w-3.5 shrink-0" />
                Handla från gården
              </span>
            )}
            {showDryck && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/85 font-medium">
                <Wine aria-hidden className="h-3.5 w-3.5 shrink-0" />
                Dryck
              </span>
            )}
          </div>
        )}

        {/* Badges row (non-producer only — producers stay calm) */}
        {!isProducer && item.badges.length > 0 && (
          <div className="flex items-center gap-2">
            {item.badges.map((b) => (
              <Badge key={b} variant="secondary" className="text-xs font-normal">{b}</Badge>
            ))}
            {item.isTrip && (
              <span className="text-xs text-muted-foreground">Ingen bokning krävs</span>
            )}
            {item.spotsInfo && item.spotsInfo !== "Fullbokat" && (
              <span className="text-xs text-amber-700">{item.spotsInfo}</span>
            )}
          </div>
        )}

        {/* Title — producers use slightly larger weight per hierarchy */}
        <h3 className={`font-semibold text-foreground line-clamp-1 ${isProducer ? "text-[16px] leading-tight" : "text-base"}`}>
          {item.title}
        </h3>

        {/* Place/location row — hidden for producers (location belongs to place page) */}
        {!isProducer && item.subtitle && (
          <div className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin aria-hidden className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1">{item.subtitle}</span>
          </div>
        )}

        {/* Time row for date_time activities */}
        {timeRow && (
          <div className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock aria-hidden className="h-3.5 w-3.5 shrink-0" />
            <span>{timeRow}</span>
          </div>
        )}

        {item.shortDescription && (
          <p className="text-sm text-foreground/85 line-clamp-2 leading-relaxed">
            {item.shortDescription}
          </p>
        )}

        {/* Price — hidden for producers */}
        {!isProducer && item.priceText && (
          <div className="pt-0.5">
            <span className="text-sm font-medium text-foreground">{item.priceText}</span>
          </div>
        )}
      </div>
    </div>
  );
}
